/* eslint-disable space-before-function-paren */
/* eslint-disable camelcase */
import { destroy, get, post, put } from '../../../api';
import { buildUserMutationBody, resolveUserEmail } from '../../../../data/User/user-identifier-utils';
import { getUserAgencyId, buildUrl } from '../../request.utils';

export const getAllUsers = async () => {
    const url = '/users';

    try {
        return await get(url);
    } catch(err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * @deprecated Backend retired `GET /users/agency-metadata`. Use team-members or
 * invitations endpoints instead. Kept for legacy tests only.
 */
export const getAdminAgenciesList = async () => {
    const url = '/users/agency-metadata?offset=0&limit=1000&account_approved=true';

    try {
        return await get(url);
    } catch(err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * @deprecated Backend retired `GET /users/agency-metadata`. Use
 * `getTeamMembersByAgency` instead. Kept for legacy tests only.
 */
export const getAgencyUsersList = async () => {
    console.log('getUserAgencyId', getUserAgencyId());
    const url = buildUrl('/users/agency-metadata', { offset: 0, limit: 1000, agencies_id: getUserAgencyId(), account_approved: true });

    try {
        return await get(url);
    } catch(err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get users for a specific agency from the /users endpoint
 * @param {string} agencyId - The agency ID to fetch users for
 * @param {Object} options - Pagination options
 * @param {number} [options.offset=0] - Offset for pagination
 * @param {number} [options.limit=100] - Limit for pagination
 * @returns {Promise<Object>} - The users data for the agency
 */
export const getUsersByAgencyId = async (agencyId, { offset = 0, limit = 100 } = {}) => {
    const url = buildUrl('/users', { agencies_id: agencyId, offset, limit });

    try {
        return await get(url);
    } catch(err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get team members (users + invites) for an agency with server-side pagination, sorting, and filtering.
 * Returns a mixed list where each item has an `is_prospect` boolean to distinguish invites from users.
 *
 * @param {Object} params - Query parameters
 * @param {string} [params.searchTerm] - Search term to filter by name/email
 * @param {number} [params.page=1] - Page number (1-based)
 * @param {number} [params.limit=10] - Items per page
 * @param {string} [params.sortBy] - Column to sort by: 'name' | 'role' | 'last_active'
 * @param {string} [params.sortOrder='asc'] - Sort direction: 'asc' | 'desc'
 * @param {string} [params.roleFilter] - Exact role to filter by, e.g. 'traffic_viewer'
 * @returns {Promise<Object>} - { data: { pagination: { offset, limit, count }, data: Array<UserOrInvite> } }
 */
export const getTeamMembersByAgency = async ({
    agencyId,
    searchTerm,
    offset,
    limit = 10,
    sortBy,
    sortOrder,
    roleFilter,
} = {}) => {
    const params = new URLSearchParams();

    if (agencyId) params.append('agency_id', agencyId);
    if (searchTerm) params.append('search_term', searchTerm);
    if (offset != null) params.append('offset', String(offset));
    if (limit) params.append('limit', String(limit));
    if (sortBy) {
        params.append('sortKey', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
    }
    if (roleFilter) params.append('role', roleFilter);

    const queryString = params.toString();
    const url = `/users/agency/team-members${queryString ? `?${queryString}` : ''}`;

    try {
        return await get(url);
    } catch (err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get LYT internal team members (users + invites) with server-side pagination, sorting, and filtering.
 * Same response shape as getTeamMembersByAgency but scoped to LYT internal users.
 *
 * @param {Object} params - Query parameters
 * @param {string} [params.searchTerm] - Search term to filter by name/email
 * @param {number} [params.offset] - Zero-based offset for pagination
 * @param {number} [params.limit=10] - Items per page
 * @param {string} [params.sortBy] - Column to sort by: 'name' | 'role' | 'last_active'
 * @param {string} [params.sortOrder='asc'] - Sort direction: 'asc' | 'desc'
 * @param {string} [params.roleFilter] - Exact role to filter by, e.g. 'lyt_admin'
 * @returns {Promise<Object>} - { data: { pagination: { offset, limit, count }, data: Array<UserOrInvite> } }
 */
export const getLytTeamMembers = async ({
    searchTerm,
    offset,
    limit = 10,
    sortBy,
    sortOrder,
    roleFilter,
} = {}) => {
    const params = new URLSearchParams();

    if (searchTerm) params.append('search_term', searchTerm);
    if (offset != null) params.append('offset', String(offset));
    if (limit) params.append('limit', String(limit));
    if (sortBy) {
        params.append('sortKey', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
    }
    if (roleFilter) params.append('role', roleFilter);

    const queryString = params.toString();
    const url = `/users/lyt/team-members${queryString ? `?${queryString}` : ''}`;

    try {
        return await get(url);
    } catch (err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get detailed information for a specific team member by email and agency.
 * Used by the User Details modal for admin/owner users to fetch
 * additional data (e.g. role history) beyond what the list endpoint provides.
 *
 * @param {Object} params - Query parameters
 * @param {string} params.userEmail - The user's email address
 * @param {string} params.agencyId - The agency ID
 * @returns {Promise<Object>} - User detail object
 */
export const getTeamMemberDetail = async ({ userEmail, agencyId } = {}) => {
    const params = new URLSearchParams();

    if (userEmail) params.append('user_email', userEmail);
    if (agencyId) params.append('agency_id', agencyId);

    const queryString = params.toString();
    const url = `/users/agency/team-members/detail${queryString ? `?${queryString}` : ''}`;

    try {
        return await get(url);
    } catch (err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get detailed information for a specific LYT internal team member by email.
 * Used by the User Details modal when opened from the LYT Team Management page.
 *
 * @param {Object} params - Query parameters
 * @param {string} params.userEmail - The user's email address
 * @returns {Promise<Object>} - User detail object
 */
export const getLytTeamMemberDetail = async ({ userEmail } = {}) => {
    const params = new URLSearchParams();

    if (userEmail) params.append('user_email', userEmail);

    const queryString = params.toString();
    const url = `/users/lyt/team-members/detail${queryString ? `?${queryString}` : ''}`;

    try {
        return await get(url);
    } catch (err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get pending invitations (requests) for a specific agency
 * @param {Object} options - Query options
 * @param {string} options.agencyId - Agency ID to filter by
 * @param {string} [options.searchTerm] - Free-text search
 * @param {number} [options.limit=10] - Rows per page
 * @param {string} [options.sortBy] - 'name' | 'role' | 'last_active'
 * @param {string} [options.sortOrder] - 'asc' | 'desc'
 * @param {string} [options.roleFilter] - Exact role value
 * @returns {Promise<Object>} - { data, pagination }
 */
export const getInvitationsByAgency = async ({
    agencyId,
    searchTerm,
    offset,
    limit = 10,
    sortBy,
    sortOrder,
    roleFilter,
} = {}) => {
    const params = new URLSearchParams();

    if (agencyId) params.append('agency_id', agencyId);
    if (searchTerm) params.append('search_term', searchTerm);
    if (offset != null) params.append('offset', String(offset));
    if (limit) params.append('limit', String(limit));
    if (sortBy) {
        params.append('sortKey', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
    }
    if (roleFilter) params.append('role', roleFilter);

    const queryString = params.toString();
    const url = `/invitations${queryString ? `?${queryString}` : ''}`;

    try {
        return await get(url);
    } catch (err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get users with agency metadata for a specific agency
 * Returns additional info like jurisdiction/city data
 * @param {string} agencyId - The agency ID to fetch users for
 * @returns {Promise<Object>} - The users data with agency metadata
 */
/**
 * @deprecated Backend retired `GET /users/agency-metadata`. Kept for legacy tests only.
 */
export const getUsersAgencyMetadataByAgencyId = async (agencyId) => {
    const url = buildUrl('/users/agency-metadata', { agencies_id: agencyId });

    try {
        return await get(url);
    } catch(err) {
        console.error(err);
        throw err.message;
    }
};

/**
 * Get pending requests (unapproved users) for a specific agency
 * @param {string} agencyId - The agency ID to fetch pending requests for
 * @returns {Promise<Array>} - Array of pending request objects
 */
/**
 * @deprecated Backend retired `GET /users/agency-metadata`. Use
 * `getInvitationsByAgency` instead. Kept for legacy tests only.
 */
export const getPendingRequestsByAgencyId = async (agencyId) => {
    const url = buildUrl('/users/agency-metadata', { agencies_id: agencyId, account_approved: false });

    try {
        const response = await get(url);
        // Return the data array of pending requests, filtered to ensure account_approved is false
        const data = response?.data || [];
        // Additional client-side filter to ensure we only return unapproved accounts
        return data.filter(user => user.account_approved === false);
    } catch(err) {
        console.error(err);
        throw err.message;
    }
};

export const updateUser = async user => {
    const url = '/users/user';

    const body = {
        ...user
    };

    try {
        const response = await put(url, body);

        return response;
    } catch(err) {
        console.error(err);
        throw err;
    }
};

/**
 * Remove a user account. Accepts `{ user_email, created_at_ts }` or a legacy
 * email string. Replaces path-based `DELETE /users/:id`.
 *
 * @param {string|Object} identifier - Email string or mutation params
 * @returns {Promise<Object>}
 */
export const removeUserByID = async (identifier) => {
    const body = typeof identifier === 'string'
        ? buildUserMutationBody({ user_email: identifier })
        : buildUserMutationBody(identifier);

    try {
        return await destroy('/users', { data: body });
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Fallback role/city update endpoint. Prefer dedicated `/users` role-change
 * endpoints for new flows when available.
 */
export const setUserRoleOrApprovedCities = async (params = {}) => {
    const url = '/roles/upsert-user-role';
    const body = buildUserMutationBody(params);

    if (body.role) {
        body.role = body.role.toLowerCase();
    }

    try {
        return await put(url, body);
    } catch (err) {
        throw err.response?.data || err;
    }
};

export const setUserApprovedAgencies = async (params = {}) => {
    const url = '/users/new-agency-assignment';
    const body = buildUserMutationBody(params);

    try {
        return await post(url, body);
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Remove a user's agency membership. Replaces path-based
 * `DELETE /users/:user_id/agencies/:agency_id/cities/:city_id`.
 */
export const removeUserApprovedCityOrAgency = async (params = {}) => {
    return removeAgencyMembership({
        user_email: resolveUserEmail(params),
        agency_id: params.agency_id ?? params.agencyID,
        created_at_ts: params.created_at_ts,
    });
};

export const removeAgencyMembership = async ({ user_email, agency_id, created_at_ts }) => {
    const url = '/users/remove-agency-membership';
    const body = { user_email, agency_id, created_at_ts };
    try {
        const { data } = await destroy(url, { data: body });
        return data;
    } catch(err) {
        throw err.response?.data || err;
    }
};

/**
 * Remove a user's membership from the LYT internal team.
 *
 * @param {Object} params
 * @param {string} params.user_email    - Email of the LYT user to remove
 * @param {string} params.created_at_ts - ISO timestamp of account creation
 * @returns {Promise<Object>}
 */
export const removeLytMembership = async ({ user_email, created_at_ts }) => {
    const url = '/users/lyt/remove-membership';
    const body = { user_email, created_at_ts };
    try {
        const { data } = await destroy(url, { data: body });
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};
